/**
 * Clubbed "otherIncomes" source (employer certificates, contracts, invoices…).
 *
 * Items come from tenantExtraData.forensic_other_income_items (built in the
 * backend from Income.dataInJSON.otherIncomes). Landlords choose which are
 * clubbed; stored in sharedProfile.income_selection:
 *   income_selection[tenantId].tenant_components.otherIncomes = [ids]
 *   income_selection[tenantId].guarantor_components[gid].otherIncomes = [ids]
 * Amount = plain sum of the chosen baseAmounts, taken as monthly.
 * Default = nothing selected (other incomes were never counted before).
 *
 * Kept free of imports from ./common to avoid a circular dependency.
 */

export const OTHER_INCOMES_SOURCE = "otherIncomes";

// same as getUpdatedExchangeIncome in ./common
const toEur = (amount, currency, originalUser) => {
  const rate = originalUser?.common?.currency_exchange?.rates?.[currency] ?? 1;
  return Math.round(amount * rate);
};

const toNumberOrNull = (v) =>
  v === null || v === undefined || v === "" || !Number.isFinite(Number(v))
    ? null
    : Number(v);

/**
 * Same as buildOtherIncomeItems in the backend (uno-backend utils/common.js)
 * — used when the items are not stored yet but forensic key factors are.
 */
export const buildOtherIncomeItemsFromForensic = (forensicAnalysis) => {
  const others = forensicAnalysis?.Income?.dataInJSON?.otherIncomes ?? [];
  const seen = {};
  return (Array.isArray(others) ? others : [])
    .filter((o) => o && typeof o === "object")
    .map((o) => {
      const amount = toNumberOrNull(o?.baseAmount);
      const baseId = `${o?.sourceKind ?? ""}|${o?.name ?? ""}|${o?.date ?? ""}|${
        amount === null ? "" : Math.round(amount)
      }`;
      seen[baseId] = (seen[baseId] ?? 0) + 1;
      return {
        id: seen[baseId] > 1 ? `${baseId}#${seen[baseId]}` : baseId,
        name: o?.name ?? null,
        source_kind: o?.sourceKind ?? null,
        date: o?.date ?? null,
        amount: amount === null ? null : Math.round(amount),
        currency: o?.currencyCode ?? null,
        frequency: o?.frequency ?? null,
        document_ids: Array.isArray(o?.foundInDocuments)
          ? o.foundInDocuments.filter((d) => typeof d === "string")
          : [],
      };
    });
};

// stored items, else built from forensic key factors, else []
// entries whose amount is missing or 0 are not shown
const hasAmount = (i) =>
  Number.isFinite(Number(i?.amount)) &&
  Number(i.amount) !== 0 &&
  i?.amount !== null;

export const getOtherIncomeItems = (user, forensicAnalysis = null) => {
  const stored = user?.tenant_extra_field_id?.forensic_other_income_items;
  if (Array.isArray(stored)) return stored.filter(hasAmount);
  if (forensicAnalysis)
    return buildOtherIncomeItemsFromForensic(forensicAnalysis).filter(
      hasAmount
    );
  return [];
};

// saved ids (stale ones dropped); default = nothing selected
export const resolveOtherIncomeIds = (items = [], savedIds = null) => {
  if (!Array.isArray(savedIds)) return [];
  const existing = new Set(items.map((i) => i.id));
  return savedIds.filter((id) => existing.has(id));
};

// plain sum of the chosen baseAmounts (monthly), converted to EUR
export const getOtherIncomeGroupAmount = (
  items = [],
  ids = [],
  originalUser
) => {
  const selected = new Set(ids);
  return items
    .filter((i) => selected.has(i.id))
    .reduce(
      (sum, i) => sum + (toEur(i.amount ?? 0, i.currency, originalUser) || 0),
      0
    );
};
